import type { Payment, QuoteDocument } from 'paintscout'
import { parseNumber } from '../../../calculator'
import { isWisetackPayment } from './is-wisetack-payment'

export type PaymentStage = 'Deposit' | 'Progress' | 'Final'

/**
 * Determines the "stage" label shown for a payment (or payment request) in the payments tables.
 *
 * - Refunds aren't a stage of the payment plan, so they return `null`.
 * - A request/payment that covers the entire remaining balance is treated as the final payment,
 *   even while still pending — pending amounts aren't yet reflected in `balance_due`, so we
 *   compare the payment amount against it directly.
 */
export function getPaymentStage(args: { payment: Payment; quote: Partial<QuoteDocument> }): PaymentStage | null {
  const { payment, quote } = args

  // don't show stage for refunds
  if (payment.forPaymentId || payment.status === 'refunded') return null

  // A Wisetack loan payout isn't a step of the merchant's payment plan, so it only ever shows
  // "Deposit" — never Progress/Final (ENG-1724).
  if (isWisetackPayment(payment)) return payment.deposit ? 'Deposit' : null

  if (payment.final) return 'Final'
  if (payment.deposit) return 'Deposit'

  const balanceDue = parseNumber(quote?.totals?.balance_due ?? 0)
  const amount = parseNumber(payment.amount)

  // Only pending/processing payments are excluded from balance_due, so only they can be eagerly
  // marked final before they land — a `paid` payment's amount is already folded in (comparing it
  // the same way would be meaningless once settled and wrong beforehand), and a `failed` payment
  // never actually reduced the balance, so it shouldn't be labeled final just because its
  // attempted amount would have covered it.
  if ((payment.status === 'pending' || payment.status === 'processing') && balanceDue > 0 && amount >= balanceDue)
    return 'Final'

  return 'Progress'
}
